<template>
  <PopUp :model-value="isOpen" size="sm" @update:model-value="v => !v && emit('close')">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 pt-6 pb-4 border-b border-black/5">
      <h3 class="text-[1.7rem] font-bold text-[#0f1f2e]">{{ t("guestJoinTitle") }}</h3>
      <button class="text-[#9ab4c0] hover:text-[#0f1f2e] transition" @click="emit('close')">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>

    <!-- Options -->
    <div v-if="guestJoinState === 'options'" class="p-6 flex flex-col gap-3">
      <p class="text-[1.35rem] text-[#627282] mb-2">{{ t("guestJoinSubtitle") }}</p>
      <button
        class="w-full py-3 rounded-xl bg-[#0f1f2e] text-white text-[1.4rem] font-semibold hover:opacity-90 transition"
        @click="emit('login')"
      >{{ t("guestJoinLogin") }}</button>
      <button
        class="w-full py-3 rounded-xl border border-[#0f1f2e] text-[#0f1f2e] text-[1.4rem] font-semibold hover:bg-slate-50 transition"
        @click="emit('register')"
      >{{ t("guestJoinRegister") }}</button>
      <button
        class="w-full py-3 rounded-xl border border-black/10 text-[#627282] text-[1.4rem] font-medium hover:bg-slate-50 transition"
        @click="emit('update:guestJoinState', 'email')"
      >{{ t("guestJoinGuest") }}</button>
    </div>

    <!-- Email input -->
    <div v-else-if="guestJoinState === 'email'" class="p-6 flex flex-col gap-4">
      <input
        :value="guestJoinEmail"
        type="email"
        class="w-full rounded-xl border border-black/15 px-4 py-3 text-[1.4rem] text-[#1a2e3b] placeholder-[#9ab4c0] outline-none focus:border-[#0f1f2e] transition"
        :placeholder="t('guestJoinEmailPlaceholder')"
        @input="emit('update:guestJoinEmail', ($event.target as HTMLInputElement).value)"
        @keydown.enter="emit('submit-email')"
      />
      <p v-if="guestJoinError" class="text-red-500 text-[1.3rem]">{{ guestJoinError }}</p>
      <button
        class="w-full py-3 rounded-xl bg-[#0f1f2e] text-white text-[1.4rem] font-semibold hover:opacity-90 transition disabled:opacity-40"
        :disabled="guestJoinLoading || !guestJoinEmail.trim()"
        @click="emit('submit-email')"
      >{{ guestJoinLoading ? t("guestJoinSending") : t("guestJoinSend") }}</button>
      <button class="text-[1.3rem] text-[#9ab4c0] hover:text-[#0f1f2e] transition" @click="emit('update:guestJoinState', 'options')">← {{ t("back") }}</button>
    </div>

    <!-- Sent confirmation -->
    <div v-else-if="guestJoinState === 'sent'" class="p-6 text-center flex flex-col items-center gap-3">
      <div class="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-1">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2.5" stroke-linecap="round"><path d="M20 6L9 17l-5-5"/></svg>
      </div>
      <h4 class="text-[1.7rem] font-bold text-[#0f1f2e]">{{ t("guestJoinSentTitle") }}</h4>
      <p class="text-[1.35rem] text-[#627282]">{{ t("guestJoinSentBody") }}</p>
      <button
        class="mt-3 px-6 py-3 rounded-xl bg-[#0f1f2e] text-white text-[1.4rem] font-semibold hover:opacity-90 transition"
        @click="emit('close')"
      >{{ t("close") }}</button>
    </div>
  </PopUp>
</template>

<script setup lang="ts">
import { useLiveGroups } from "@/components/companionGroups/useLiveGroups";
import PopUp from "@/components/popup/PopUp.vue";

defineProps<{
  isOpen: boolean;
  guestJoinState: "options" | "email" | "sent";
  guestJoinEmail: string;
  guestJoinLoading: boolean;
  guestJoinError: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "login"): void;
  (e: "register"): void;
  (e: "update:guestJoinEmail", value: string): void;
  (e: "update:guestJoinState", value: "options" | "email" | "sent"): void;
  (e: "submit-email"): void;
}>();

const { t } = useLiveGroups(
  { _skipInit: true },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  (_e: any, ..._args: any[]) => {},
);
</script>
